/**
 * The app's chrome: a top bar that is always there (logo → home, settings, profile) and,
 * for signed-in people, the main section navigation underneath.
 *
 * Sections are data, so adding one is a one-line change. `roles` (optional) hides a section
 * from people who don't have any of the listed roles.
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { UserDropdown } from '#app/components/user-dropdown.tsx'
import { isPublicPagePath } from '#app/utils/qr-path.ts'
import { useOptionalUser, userHasRole } from '#app/utils/user.ts'
import { Button } from './ui/button.tsx'

export type AppSection = {
	to: string
	label: string
	icon: IconName
	/** show only to users holding at least one of these roles */
	roles?: Array<string>
	/** short blurb used by the section landing pages */
	blurb?: string
}

export const APP_SECTIONS: Array<AppSection> = [
	{
		to: '/dashboard',
		label: 'Dashboard',
		icon: 'layout-dashboard',
		blurb: 'Your day at a glance — tailored to your role.',
	},
	{
		to: '/search',
		label: 'Search',
		icon: 'magnifying-glass',
		blurb: 'Find a customer or search the calls available to you.',
	},
	{
		to: '/calls',
		label: 'Calls',
		icon: 'phone-incoming',
		blurb:
			'Live and recent calls with their timelines. Inbound calls also pop into the work sidebar wherever you are.',
	},
	{
		// everyone: routing, counts and agent states are for all; callers and
		// transcripts are managers-only, enforced on the server
		to: '/command-center',
		label: 'Command Center',
		icon: 'phone',
		blurb:
			'Every department’s call ladder, the shared spine and the AI front door, live — and any routing misconfiguration.',
	},
	{
		to: '/texts',
		label: 'Texts',
		icon: 'chat-bubble',
		blurb:
			'Customer text conversations — claim from the pool, reply with full customer context.',
	},
	{
		to: '/job-booking',
		label: 'Create Job',
		icon: 'calendar',
		blurb: 'Create a ServiceTitan job through the guided intake flow.',
	},
	{
		to: '/bookings',
		label: 'Bookings',
		icon: 'envelope-closed',
		blurb:
			'Job requests from web forms and booking providers — claim one, book the job, or dismiss it with a reason.',
	},
	{
		to: '/dispatch',
		label: 'Dispatch',
		icon: 'clock',
		blurb: "Today's board — who's where, on what job.",
	},
	{
		to: '/tasks',
		label: 'Tasks',
		icon: 'list-checks',
		blurb: 'Follow-ups, complaints, and anything that needs an owner.',
	},
	{
		to: '/purchase-orders',
		label: 'Purchase Orders',
		icon: 'file-text',
		blurb:
			'Search, create, receive, and cancel ServiceTitan purchase orders — live from ServiceTitan.',
	},
	{
		to: '/report-issue',
		label: 'Report an issue',
		icon: 'question-mark-circled',
		blurb:
			'Something in the app not working the way it needs to? Report it here — admins review every report and you can watch its status.',
	},
	{
		// managers upload the lists; the CSRs claim the calls from their task panel
		to: '/outbound',
		label: 'Outbound calls',
		icon: 'phone',
		roles: ['admin', 'manager'],
		blurb:
			'Upload a spreadsheet of customers, tell the CSRs what to do on the calls, set a pace, and follow how it goes.',
	},
	{
		to: '/data-health',
		label: 'Data Health',
		icon: 'update',
		roles: ['admin', 'manager'],
		blurb:
			'Company job KPIs and per-table ServiceTitan sync freshness — the management view of the data estate.',
	},
	{
		// everyone: a CSR reads the current rules here, a manager sets them. The
		// route decides which view; the service decides what each may read.
		to: '/sop',
		label: 'SOP',
		icon: 'clipboard-list',
		blurb:
			'Standard operating procedures — the rules managers set for the whole company. Start with the services we do and do not offer.',
	},
]

export function AppHeader({ banner }: { banner?: React.ReactNode }) {
	const user = useOptionalUser()
	const { pathname } = useLocation()
	// The public Lowe's form, and a mistyped address under the public QR pages
	// (their own pages draw no app header at all): a member of the public.
	const publicPage = isPublicPagePath(pathname)
	const mark = (
		<>
			<HepMark />
			{/* The staff app's name; a customer on the public form sees just the logo. */}
			{publicPage ? null : (
				<span className="bg-hep-yellow text-hep-yellow-foreground rounded-full px-4 py-1.5 text-lg leading-none font-black tracking-tight shadow-sm transition group-hover:brightness-95">
					Everything
				</span>
			)}
		</>
	)
	return (
		<header className="bg-hep-black sticky top-0 z-40 text-white">
			{banner}
			<div className="flex min-h-14 flex-wrap items-center gap-3 px-3 py-2 sm:px-4">
				{publicPage ? (
					// Home is the staff page ("Sign in…"): the public form does not link there.
					<div className="flex items-center gap-3">{mark}</div>
				) : (
					<Link
						to="/"
						className="group flex items-center gap-3"
						aria-label="HEP home"
					>
						{mark}
					</Link>
				)}

				{/* section nav now lives inside the workspace (see work-tabs.tsx WorkspaceNav) */}

				<div className="ml-auto flex max-w-full min-w-0 items-center gap-1">
					{user ? (
						<>
							<Button
								asChild
								variant="ghost"
								size="icon"
								className="size-9 text-white hover:bg-white/10 hover:text-white"
								title="Settings"
							>
								<Link to="/settings/profile" aria-label="Settings">
									<Icon name="gear" size="md" />
								</Link>
							</Button>
							{userHasRole(user ?? null, 'admin') ? (
								<Button
									asChild
									variant="ghost"
									size="sm"
									className="hidden text-white hover:bg-white/10 hover:text-white sm:inline-flex"
								>
									<Link to="/admin">Admin</Link>
								</Button>
							) : null}
							<UserDropdown />
						</>
					) : !publicPage ? (
						<Button asChild size="sm">
							<Link to="/login">Log in</Link>
						</Button>
					) : null}
				</div>
			</div>
		</header>
	)
}

/** The footer's label. The public Lowe's form (or /qr) is not an "internal app". */
export function AppFooterNote() {
	const { pathname } = useLocation()
	return (
		<span>{isPublicPagePath(pathname) ? 'HEP' : 'HEP · internal apps'}</span>
	)
}

/**
 * The HEP logo. Uses the real artwork at /img/brand/hep-logo.png (drop the transparent PNG into
 * public/img/brand/) and falls back to a CSS stand-in if the file isn't there.
 * Shared with the basic caller-ID shell (routes/callerid/_layout.tsx).
 */
export function HepMark() {
	const [broken, setBroken] = useState(false)
	// onError misses failures that happen before hydration — double-check on mount
	const check = (el: HTMLImageElement | null) => {
		if (el && el.complete && el.naturalWidth === 0) setBroken(true)
	}
	if (!broken) {
		return (
			<span className="grid h-10 place-items-center rounded-md border border-black/60 bg-white px-1.5 shadow-sm transition group-hover:scale-[1.03]">
				<img
					ref={check}
					src="/img/brand/hep-logo.png"
					alt="HEP is on the way — HEP, Inc."
					className="h-8 w-auto"
					onError={() => setBroken(true)}
				/>
			</span>
		)
	}
	return (
		<span
			aria-hidden
			className="grid h-10 w-[4.5rem] place-items-center rounded-md border border-black/60 bg-white leading-none text-black shadow-sm transition group-hover:scale-[1.03]"
		>
			<span className="text-[6px] font-bold tracking-wide uppercase">
				HEP is on the way
			</span>
			<span className="text-hep-red text-xl leading-none font-black tracking-tight italic">
				HEP
			</span>
			<span className="text-[6px] font-bold">, INC.</span>
		</span>
	)
}
